/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/set-state-in-effect */
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import {
  getConversationMode,
  resolveSelectableMode,
  saveConversationMode,
} from '@/sections/insights/aiAssistant/config/storage';
import { LAST_ACTIVE_CONVERSATION_KEY } from '@/sections/insights/aiAssistant/constants/keys';
import type {
  AIAssistantMode,
  SidebarTab,
} from '@/sections/insights/aiAssistant/types/conversation.types';
import {
  getTenantScopedValue,
  removeTenantScopedValue,
  setTenantScopedValue,
} from '@/utils/storage/tenantScoping.util';

interface HistorySetters {
  setMessageHistory: Dispatch<SetStateAction<string[]>>;
  setHistoryIndex: Dispatch<SetStateAction<number>>;
  setSavedMessage: Dispatch<SetStateAction<string>>;
}

interface MessagingSetters {
  setFailedMessage: Dispatch<SetStateAction<string | null>>;
  setIsWaitingForResponse: Dispatch<SetStateAction<boolean>>;
}

interface UseConversationSwitchProps {
  conversationId: string | null;
  mode: AIAssistantMode;
  setMode: Dispatch<SetStateAction<AIAssistantMode>>;
  activeAgent: string | null;
  setActiveAgent: Dispatch<SetStateAction<string | null>>;
  setActiveTab: Dispatch<SetStateAction<SidebarTab>>;
  canUseAgentMode: boolean;
  isAIDisabledButBypassed: boolean;
  disableAIInference: boolean;
  summonMessage: string | null;
  messages: any[];
  messagesLoading: boolean;
  modeChangeRef: MutableRefObject<'auto' | null>;
  historySetters: HistorySetters;
  messagingSetters: MessagingSetters;
}

export function useConversationSwitch({
  conversationId,
  mode,
  setMode,
  activeAgent,
  setActiveAgent,
  setActiveTab,
  canUseAgentMode,
  isAIDisabledButBypassed,
  disableAIInference,
  summonMessage,
  messages,
  messagesLoading,
  modeChangeRef,
  historySetters: { setMessageHistory, setHistoryIndex, setSavedMessage },
  messagingSetters: { setFailedMessage, setIsWaitingForResponse },
}: UseConversationSwitchProps) {
  const [, setSearchParams] = useSearchParams();
  const [previousConversationId, setPreviousConversationId] = useState<
    string | null
  >(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // When AI inference is disabled, clear any active conversation to prevent
  // getting stuck in a "Loading conversation..." loop that never resolves
  useEffect(() => {
    if (isAIDisabledButBypassed && conversationId) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('conversationId');
          return newParams;
        },
        { replace: true }
      );
      setIsLoadingConversation(false);
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [isAIDisabledButBypassed, conversationId, setSearchParams]);

  // Restore last active conversation from tenant-scoped localStorage on mount
  useEffect(() => {
    if (!conversationId && !summonMessage && !disableAIInference) {
      const lastConversationId = getTenantScopedValue<string>(
        LAST_ACTIVE_CONVERSATION_KEY
      );
      const UUID_REGEX =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (lastConversationId && UUID_REGEX.test(lastConversationId)) {
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', lastConversationId);
            return newParams;
          },
          { replace: true }
        );
      }
    }
  }, []); // Mount-only: reads URL params once to restore session. Post-mount flag changes handled by isAIDisabledButBypassed effect above.

  // Track conversation switching and clear states to prevent flash
  useEffect(() => {
    if (conversationId !== previousConversationId) {
      if (conversationId) {
        setIsLoadingConversation(true);
        modeChangeRef.current = null;
        // Restore mode and agent for this specific conversation
        const savedState = getConversationMode(conversationId);
        const restoredMode = resolveSelectableMode(savedState, {
          canUseAgentMode,
        });
        setMode(restoredMode);
        setActiveAgent(savedState.agent || null);
        // resolveSelectableMode never returns 'auto', so a restored sidebar
        // conversation always lands on the conversations tab (never hunts).
        setActiveTab('conversations');
      } else {
        // When clearing conversation (new chat), reset to target mode
        if (modeChangeRef.current) {
          setMode(modeChangeRef.current);
          modeChangeRef.current = null;
        } else {
          setMode('query');
          setActiveTab('conversations');
        }
        setActiveAgent(null);
      }
      // Clear any pending states to prevent content flash when switching conversations
      setFailedMessage(null);
      setIsWaitingForResponse(false);
      // Reset history navigation state when switching conversations
      setMessageHistory([]);
      setHistoryIndex(-1);
      setSavedMessage('');
    }
    setPreviousConversationId(conversationId);
  }, [conversationId, previousConversationId, canUseAgentMode]);

  // Stop loading when messages arrive or when no conversation is selected
  // Also populate message history from loaded conversation
  useEffect(() => {
    if (
      (messages.length > 0 || !messagesLoading || !conversationId) &&
      isLoadingConversation
    ) {
      setIsLoadingConversation(false);
      if (messages.length > 0) {
        const userMessages = messages
          .filter(msg => msg.role === 'user' && msg.content)
          .map(msg => msg.content);
        if (userMessages.length > 0) {
          setMessageHistory(userMessages);
          setHistoryIndex(-1);
          setSavedMessage('');
        }
      }
    }
  }, [messages.length, messagesLoading, conversationId, isLoadingConversation]);

  // Save mode and active agent to localStorage for current conversation whenever they change
  useEffect(() => {
    if (conversationId) {
      // 'auto' is the hunt-section app mode, not a per-chat-conversation mode.
      // conversationId always points at a chat conversation, so persisting
      // 'auto' here corrupts that conversation (it would later restore into the
      // hunt section). Never persist 'auto'; the active hunt lives in
      // useAutoDrawerStore, not in per-conversation mode storage.
      if (mode !== 'auto') {
        saveConversationMode(conversationId, mode, activeAgent || undefined);
      }
    }
  }, [mode, activeAgent, conversationId]);

  // Reset to query mode if agent mode becomes unavailable (e.g., flag toggled off)
  useEffect(() => {
    if (!canUseAgentMode && mode === 'agent') {
      setMode('query');
      setActiveAgent(null);
    }
  }, [canUseAgentMode, mode]);

  // Persist active conversation to tenant-scoped localStorage for session restore
  useEffect(() => {
    if (conversationId) {
      setTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY, conversationId);
    } else {
      removeTenantScopedValue(LAST_ACTIVE_CONVERSATION_KEY);
    }
  }, [conversationId]);

  return {
    isLoadingConversation,
  };
}
